import { Slot } from 'radix-ui'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Spinner } from './spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'md' | 'sm'

const base =
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-sm border font-medium whitespace-nowrap select-none ' +
  'transition-colors duration-100 disabled:pointer-events-none disabled:opacity-55 aria-disabled:pointer-events-none aria-disabled:opacity-55 ' +
  '[&_svg]:size-4 [&_svg]:shrink-0'

const variants: Record<ButtonVariant, string> = {
  primary: 'border-stamp bg-stamp text-on-accent hover:bg-stamp/88',
  secondary: 'border-rule-strong bg-surface text-ink hover:bg-surface-sunken',
  ghost: 'border-transparent bg-transparent text-ink hover:bg-surface-sunken',
  danger: 'border-ink-red bg-ink-red text-on-accent hover:bg-ink-red/88',
}

const sizes: Record<ButtonSize, string> = {
  md: 'h-(--control-h) px-3 text-sm',
  sm: 'h-7 px-2.5 text-sm',
}

export function buttonClasses(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className?: string,
): string {
  return cn(base, variants[variant], sizes[size], className)
}

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Render the child element (e.g. a router Link) with button styling. */
  asChild?: boolean
  /** Shows a spinner, disables the button and marks it busy. Ignored with asChild. */
  loading?: boolean
  /** Leading icon. */
  icon?: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'md',
  asChild = false,
  loading = false,
  icon,
  className,
  children,
  disabled,
  type,
  ...props
}: ButtonProps) {
  const classes = buttonClasses(variant, size, className)
  if (asChild) {
    return (
      <Slot.Root className={classes} data-variant={variant} {...props}>
        {children}
      </Slot.Root>
    )
  }
  return (
    <button
      type={type ?? 'button'}
      className={classes}
      data-variant={variant}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  )
}
